import { groupLabel } from '@/components/usergroup/group'
import './common.css'

import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { addServerErrors } from '@/components/forms/common/FormBase'
import { RadiusPicker, RadiusPickerValue } from '@/components/Map/RadiusPicker'
import { Spinner } from '@/components/spinner'
import { useAppContext } from '@/context/app'

export interface SimpleUserGroup {
  id: string
  name: string
}

export interface GeoAlertRow {
  id: string
  usersGroupId: string
  machinesGroupId: string
  name: string
  message: string
  lat: number
  lng: number
  radius: number
  alertOnEnter: boolean
  alertOnExit: boolean
  isActive: boolean
  createdAt?: string
  updatedAt?: string
}

export interface GeoAlertFormData {
  usersGroupId: string
  machinesGroupId: string
  name: string
  message: string
  isActive: boolean
  alertOnEnter: boolean
  alertOnExit: boolean
  area?: RadiusPickerValue | null
}

export const defaultGeoAlertFormData = (groupId?: string): GeoAlertFormData => ({
  usersGroupId: groupId || '',
  machinesGroupId: groupId || '',
  name: '',
  message: '',
  isActive: true,
  alertOnEnter: true,
  alertOnExit: false,
  area: null,
})

export const normalizeGeoAlertPayload = (data: GeoAlertFormData) => {
  const area = data.area
  const usersGroupId = data.usersGroupId || data.machinesGroupId
  const machinesGroupId = data.machinesGroupId || data.usersGroupId

  return {
    usersGroupId,
    machinesGroupId,
    name: data.name.trim(),
    message: data.message.trim(),
    lat: area?.lat,
    lng: area?.lng,
    radius: area?.radius,
    alertOnEnter: !!data.alertOnEnter,
    alertOnExit: !!data.alertOnExit,
    isActive: !!data.isActive,
  }
}

export const GeoAlertRemovePopup = ({
  onClose,
  geoAlert,
  onRemoved,
}: {
  onClose: () => void
  geoAlert?: GeoAlertRow
  onRemoved: () => void
}) => {
  const { services, translates } = useAppContext()
  const [error, setError] = useState<string | null>(null)

  if (!geoAlert || !translates) {
    return null
  }

  const remove = async () => {
    setError(null)
    const response = await services.session.fetchWithToken(`/api/communal/geo-alerts/byid/${geoAlert.id}/delete`, {
      method: 'DELETE',
    })
    if (response.status !== 200) {
      const message = await response.text()
      setError(message || translates.for('elements.table.geo_alerts.remove_error'))
      return
    }
    onRemoved()
    onClose()
  }

  return <div className='dialog'>
    <div className='dialog-text'>
      {translates.for('elements.table.geo_alerts.remove_ask').replace('{name}', geoAlert.name)}
    </div>
    <div className='dialog-btns'>
      <div className='dialog-btn' onClick={() => onClose()}>
        <i className='svgfont-no' />
        {translates.for('elements.dialog.yesno.no')}
      </div>
      <div className='dialog-btn' onClick={remove}>
        <i className='svgfont-yes' />
        {translates.for('elements.dialog.yesno.yes')}
      </div>
      {error ? <div className='dialog-error'>{error}</div> : null}
    </div>
  </div>
}

export const GeoAlertEditPopup = ({
  onClose,
  geoAlertId,
  groups,
  onSaved,
}: {
  onClose: () => void
  geoAlertId?: string
  groups: SimpleUserGroup[]
  onSaved: (geoAlertId?: string) => void
}) => {
  const { services, session, translates } = useAppContext()
  const form = useForm<GeoAlertFormData>({
    mode: 'onChange',
    defaultValues: defaultGeoAlertFormData(groups[0]?.id),
  })
  const [loading, setLoading] = useState<boolean>(!!geoAlertId)
  const [submitErrors, setSubmitErrors] = useState<string[]>([])

  const area = form.watch('area')
  const alertOnEnter = form.watch('alertOnEnter')
  const alertOnExit = form.watch('alertOnExit')
  const usersGroupId = form.watch('usersGroupId')
  const machinesGroupId = form.watch('machinesGroupId')

  useEffect(() => {
    const currentUsersGroupId = form.getValues('usersGroupId')
    const currentMachinesGroupId = form.getValues('machinesGroupId')
    if (!currentUsersGroupId && groups.length > 0) {
      form.setValue('usersGroupId', groups[0].id)
    }
    if (!currentMachinesGroupId && groups.length > 0) {
      form.setValue('machinesGroupId', groups[0].id)
    }
  }, [groups])

  useEffect(() => {
    if (usersGroupId && !machinesGroupId) {
      form.setValue('machinesGroupId', usersGroupId, {
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }, [usersGroupId, machinesGroupId])

  useEffect(() => {
    if (geoAlertId === undefined) {
      form.reset(defaultGeoAlertFormData(groups[0]?.id))
      setLoading(false)
      return
    }

    setLoading(true)
    services.session.fetchWithToken(`/api/communal/geo-alerts/byid/${geoAlertId}`)
      .then(async (response) => {
        if (response.status !== 200) {
          throw new Error(await response.text())
        }
        return response.json()
      })
      .then((data: GeoAlertRow) => {
        form.reset({
          usersGroupId: data.usersGroupId,
          machinesGroupId: data.machinesGroupId || data.usersGroupId,
          name: data.name || '',
          message: data.message || '',
          isActive: !!data.isActive,
          alertOnEnter: !!data.alertOnEnter,
          alertOnExit: !!data.alertOnExit,
          area: {
            lat: data.lat,
            lng: data.lng,
            radius: data.radius,
          },
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [geoAlertId, groups])

  const handle = async (data: GeoAlertFormData) => {
    setSubmitErrors([])

    const payload = normalizeGeoAlertPayload(data)
    const url = geoAlertId === undefined
      ? '/api/communal/geo-alerts'
      : `/api/communal/geo-alerts/byid/${geoAlertId}/update`
    const method = geoAlertId === undefined ? 'POST' : 'PUT'

    const response = await services.session.fetchWithToken(url, {
      method,
      body: JSON.stringify(payload),
    })

    if (response.status !== 200) {
      const errors = await response.json().catch(() => null)
      if (errors) {
        addServerErrors(form, session.translates, errors)
        const translatedErrors = Object.values(errors as Record<string, { message?: string }>).map((error) => {
          if (!error?.message) {
            return translates.for('errors.general_error')
          }
          return translates.for(error.message)
        })
        setSubmitErrors(translatedErrors)
      } else {
        const message = await response.text().catch(() => '')
        setSubmitErrors([message || translates.for('errors.general_error')])
      }
      return
    }

    const result = geoAlertId === undefined
      ? await response.json().catch(() => null)
      : null

    onSaved(result?.id)
    onClose()
  }

  const canSubmit = useMemo(() => {
    return !!area && (alertOnEnter || alertOnExit)
  }, [area, alertOnEnter, alertOnExit])

  if (!session || !translates || loading) {
    return <Spinner />
  }

  if (groups.length === 0) {
    return <div className='critic-error-box'>
      {translates.for('elements.table.geo_alerts.empty_groups')}
    </div>
  }

  return <form className='main-form geo-main-form' onSubmit={form.handleSubmit(handle)}>
    <div className='form-field'>
      <label>{translates.for('elements.table.geo_alerts.users_group')}</label>
      <select {...form.register('usersGroupId', { required: true })}>
        {groups.map((group) => (
          <option key={group.id} value={group.id}>
            {groupLabel(group)}
          </option>
        ))}
      </select>
    </div>

    <div className='form-field'>
      <label>{translates.for('elements.table.geo_alerts.machines_group')}</label>
      <select {...form.register('machinesGroupId', { required: true })}>
        {groups.map((group) => (
          <option key={group.id} value={group.id}>
            {groupLabel(group)}
          </option>
        ))}
      </select>
    </div>

    <div className='form-field'>
      <label>{translates.for('elements.table.geo_alerts.name')}</label>
      <input type='text' {...form.register('name', { required: true })} />
    </div>

    <div className='form-field'>
      <label>{translates.for('elements.table.geo_alerts.message')}</label>
      <textarea rows={4} {...form.register('message', { required: true })} />
    </div>

    <div className='form-field geo-alerts-map-field'>
      <label>{translates.for('elements.table.geo_alerts.area')}</label>
      <RadiusPicker
        value={area || null}
        onChange={(value) => {
          form.setValue('area', value, {
            shouldDirty: true,
            shouldValidate: true,
          })
        }}
      />
      <div className='coords'>
        {area
          ? translates.for('elements.table.geo_alerts.coordinates').replace('{lat}', area.lat.toFixed(6)).replace('{lng}', area.lng.toFixed(6))
          : translates.for('elements.table.geo_alerts.area_hint')}
      </div>
    </div>

    <div className='geo-alerts-toggle-grid'>
      <label className='geo-alerts-toggle'>
        <input type='checkbox' {...form.register('alertOnEnter')} />
        <span>{translates.for('elements.table.geo_alerts.alert_on_enter')}</span>
      </label>

      <label className='geo-alerts-toggle'>
        <input type='checkbox' {...form.register('alertOnExit')} />
        <span>{translates.for('elements.table.geo_alerts.alert_on_exit')}</span>
      </label>

      <label className='geo-alerts-toggle'>
        <input type='checkbox' {...form.register('isActive')} />
        <span>{translates.for('elements.table.geo_alerts.is_active')}</span>
      </label>
    </div>

    <div className='form-buttons'>
      <button className='submit-button' type='submit' disabled={!canSubmit}>
        {geoAlertId === undefined
          ? translates.for('elements.table.geo_alerts.add')
          : translates.for('elements.table.geo_alerts.save')}
      </button>
    </div>

    {!canSubmit ? <div className='critic-error-box'>
      {translates.for('elements.table.geo_alerts.validation_hint')}
    </div> : null}

    {submitErrors.length > 0 ? <div className='critic-error-box'>
      {submitErrors.map((error, index) => (
        <div key={error + index}>{error}</div>
      ))}
    </div> : null}
  </form>
}
